import { useCallback, useEffect, useMemo } from "react";
import {
  Button,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Switch,
  Text,
  Flex,
  Spacer,
  ModalCloseButton,
  Box,
  VStack,
} from "@chakra-ui/react";
import {
  EventDetailScheduledUpdatesSection,
  KYDEventScheduledUpdateSummary,
} from "@common/interfaces/KYDEvent";
import { FaCalendar, FaChevronLeft } from "react-icons/fa";
import { FormProvider, useForm } from "react-hook-form";
import LineupStep from "./steps/LineupStep";
import EventNameStep from "./steps/EventNameStep";
import DescriptionStep from "./steps/DescriptionStep";
import PosterStep from "./steps/PosterStep";
import ReviewStep from "./steps/ReviewStep";
import { useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import dayjs from "dayjs";
import { formatFullDate } from "./ScheduledUpdatesTable";
import EditScheduledModalNavigation from "./EditScheduledModalNavigation";

const platformSchema = yup.object({
  name: yup.string().optional(),
  followers: yup.number().optional(),
  display_followers: yup.string().optional(),
  url: yup.string().optional(),
  platform: yup
    .string()
    .oneOf(["instagram", "tiktok", "spotify", "isni", "bandsintown"])
    .optional(),
  id: yup.string().optional(),
});

const schema = yup.object({
  id: yup.string().optional().nullable(),
  name: yup.string().optional(),
  subtitle: yup.string().optional(),
  description: yup.string().optional(),
  description_rich: yup.object().optional(),
  date: yup.string().optional().nullable(),
  poster: yup.string().optional().nullable(),
  new_performers: yup.array().of(
    yup.object({
      instagram: platformSchema.optional(),
      spotify: platformSchema.optional(),
      tiktok: platformSchema.optional(),
      name: yup.string().optional(),
      isNew: yup.boolean().optional(),
      image_url: yup.string().optional(),
      soundcharts_id: yup.string().optional(),
      id: yup.string().required("Performer ID is required"),
      set_time: yup.string().optional(),
    })
  ),
});

export type ScheduledUpdateValues = yup.InferType<typeof schema>;

// Utility to get only changed fields compared to the original data
export function getChangedFields<T extends Record<string, any>>(
  values: T,
  original: T
): Partial<T> {
  const changed: Partial<T> = {};
  for (const key in values) {
    // For arrays/objects, do a shallow comparison
    const val = values[key];
    const orig = original[key];
    if (Array.isArray(val) && Array.isArray(orig)) {
      if (val.length !== orig.length || val.some((v, i) => v !== orig[i])) {
        changed[key] = val;
      }
    } else if (
      typeof val === "object" &&
      val !== null &&
      typeof orig === "object" &&
      orig !== null
    ) {
      if (JSON.stringify(val) !== JSON.stringify(orig)) {
        changed[key] = val;
      }
    } else {
      if (val !== orig) {
        changed[key] = val;
      }
    }
  }
  return changed;
}

const EditScheduledUpdateModal = ({
  data,
  link,
  scheduledUpdate,
  timezone,
  field,
  onClose,
  isLoading,
  onSubmit,
  isOpen = false,
}: {
  link?: string;
  timezone?: string;
  isOpen?: boolean;
  isLoading: boolean;
  onClose: () => void;
  data: Pick<
    EventDetailScheduledUpdatesSection["data"],
    | "name"
    | "subtitle"
    | "description"
    | "description_rich"
    | "poster"
    | "performers"
    | "timezone"
    | "id"
    | "scheduled_updates"
  >;
  scheduledUpdate: KYDEventScheduledUpdateSummary | null;
  onSubmit: (
    data: ScheduledUpdateValues,
    options?: { justPerformers?: boolean }
  ) => Promise<void>;
  field:
    | "name"
    | "subtitle"
    | "description"
    | "description_rich"
    | "date"
    | "poster"
    | "performers"
    | null;
}) => {
  const methods = useForm<ScheduledUpdateValues>({
    mode: "onChange",
    criteriaMode: "all",
    reValidateMode: "onChange",
    values: {
      id: scheduledUpdate ? scheduledUpdate.id : undefined,
      name: scheduledUpdate ? scheduledUpdate.name : "",
      subtitle: scheduledUpdate ? scheduledUpdate.subtitle : "",
      description: scheduledUpdate ? scheduledUpdate.description : "",
      description_rich: scheduledUpdate
        ? scheduledUpdate.description_rich
        : data.description_rich,
      date: scheduledUpdate ? scheduledUpdate.date : "",
      poster: scheduledUpdate ? scheduledUpdate.poster : "",
      new_performers: scheduledUpdate ? scheduledUpdate.performers : [],
    },
    resolver: yupResolver(schema),
  });

  const {
    trigger,
    reset,
    formState: { dirtyFields, isValid, errors },
    handleSubmit,
  } = methods;

  const _onClose = () => {
    reset();
    setCurrentStep(0);
    onClose();
  };

  const [currentStep, setCurrentStep] = useState(0);
  const steps = [
    { label: "Lineup", component: LineupStep, field: ["new_performers"] },
    {
      label: "Event Name",
      component: EventNameStep,
      field: ["name", "subtitle"],
    },
    {
      label: "Description",
      component: DescriptionStep,
      field: ["description", "description_rich"],
    },
    { label: "Poster", component: PosterStep, field: ["poster"] },
    { label: "Review", component: ReviewStep },
  ];

  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  useEffect(() => {
    if (isLastStep) {
      trigger();
    }
  }, [isLastStep]);

  const handleNext = () => {
    setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
  };
  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const date = methods.watch("date");
  const _onSubmit = async (formValues: ScheduledUpdateValues) => {
    console.log({ formValues, performers: formValues.new_performers });

    const changedFields = {
      ...(scheduledUpdate ? scheduledUpdate : {}),
      ...getChangedFields(formValues, {
        id: undefined,
        name: data.name,
        subtitle: data.subtitle,
        description: data.description,
        description_rich: data.description_rich,
        date: null,
        poster: data.poster,
        new_performers: data.performers,
      }),
    };

    if (isFirstStep && !date) {
      return onSubmit(formValues, { justPerformers: true });
    }

    if (!isLastStep) {
      handleNext();
    } else {
      onSubmit(changedFields);
    }
  };

  useEffect(() => {
    if (field) {
      const step = steps.findIndex((step) => step.field?.includes(field));
      if (step !== -1) {
        setCurrentStep(step);
      }
    }
  }, [field]);

  useEffect(() => {
    if (scheduledUpdate) {
      reset({
        id: scheduledUpdate.id,
        name: scheduledUpdate.name ? scheduledUpdate.name : data.name,
        subtitle: scheduledUpdate.subtitle
          ? scheduledUpdate.subtitle
          : data.subtitle,
        description: scheduledUpdate.description
          ? scheduledUpdate.description
          : data.description,
        description_rich: scheduledUpdate.description_rich
          ? scheduledUpdate.description_rich
          : data.description_rich,
        date: scheduledUpdate.date ? scheduledUpdate.date : undefined,
        poster: scheduledUpdate.poster ? scheduledUpdate.poster : data.poster,
        new_performers: scheduledUpdate.performers
          ? scheduledUpdate.performers
          : [],
      });
    } else {
      reset({
        id: undefined,
        name: data.name,
        subtitle: data.subtitle,
        description: data.description,
        description_rich: data.description_rich,
        date: null,
        poster: data.poster,
        new_performers: [],
      });
    }
  }, [isOpen, scheduledUpdate, data]);

  useEffect(() => {
    if (!isOpen) {
      reset();
      setCurrentStep(0);
    }
  }, [isOpen, reset]);

  const watchedValues = methods.watch();
  const hasValue = (val: any) => {
    if (Array.isArray(val)) return val.length > 0;
    if (val && typeof val === "string") return val.trim() !== "";
    if (val && typeof val === "object") return Object.keys(val).length > 0;
    return val !== undefined && val !== null && val !== "";
  };

  const new_performers = methods.watch("new_performers");

  const isStepUpdated = (step) => {
    if (!step.field) return false;

    const changedFields = getChangedFields(watchedValues, {
      id: undefined,
      name: data.name,
      subtitle: data.subtitle,
      // description: data.description,
      description_rich: data.description_rich,
      date: null,
      poster: data.poster,
      new_performers: data.performers,
    });
    return step.field.some((field) => hasValue(changedFields[field]));
  };

  const nextButtonText = useMemo(() => {
    if (isFirstStep && !date) {
      return "Add To Lineup";
    }

    if (isLastStep) {
      return "Confirm and Schedule";
    }

    return "Next";
  }, [isFirstStep, isLastStep, date]);

  return (
    <Modal
      size={["full", "2xl"]}
      isOpen={isOpen}
      onClose={_onClose}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent rounded={"2xl"}>
        <ModalHeader>
          <VStack w="full" gap={1}>
            <Text w="full">
              {scheduledUpdate
                ? "Schedule event detail updates"
                : isFirstStep
                ? "Add performers to lineup"
                : "Edit Scheduled Update"}
            </Text>

            {scheduledUpdate ? (
              <HStack
                w="full"
                justifyContent={"flex-start"}
                color="gray.500"
                fontWeight={"medium"}
                fontSize={"sm"}
              >
                <FaCalendar />
                <Text w="full">
                  {formatFullDate(
                    dayjs(scheduledUpdate.date).tz(timezone),
                    timezone
                  )}
                </Text>
              </HStack>
            ) : (
              <HStack
                w="full"
                justifyContent={"flex-start"}
                color="gray.500"
                fontWeight={"medium"}
                fontSize={"sm"}
              >
                <Text w="full">Set time and announcement date</Text>
              </HStack>
            )}
          </VStack>
        </ModalHeader>
        <ModalCloseButton onClick={_onClose} />
        <ModalBody>
          <FormProvider {...methods}>
            <Stack spacing={4} w="full">
              {!isFirstStep && (
                <EditScheduledModalNavigation
                  steps={steps}
                  currentStep={currentStep}
                  setCurrentStep={setCurrentStep}
                  isStepUpdated={isStepUpdated}
                />
              )}
              {(() => {
                const StepComponent = steps[currentStep].component;
                return (
                  <StepComponent
                    mode={scheduledUpdate ? "edit" : "create"}
                    data={data}
                    isLoading={isLoading}
                  />
                );
              })()}
            </Stack>
          </FormProvider>
        </ModalBody>
        <ModalFooter justifyContent={"space-between"}>
          <Flex w="full">
            {!isFirstStep && (
              <Button onClick={handleBack} mr="2" variant={"outline"}>
                <HStack gap={2}>
                  <Icon as={FaChevronLeft} boxSize={4} fontSize={"sm"} />
                  <Text fontWeight={"normal"}>
                    {steps[currentStep - 1].label}
                  </Text>
                </HStack>
              </Button>
            )}
            <Spacer />
            <HStack>
              <Button
                px={8}
                color="white"
                bg="green.500"
                isLoading={isLoading}
                isDisabled={
                  isFirstStep ? new_performers.length === 0 : !isValid
                }
                onClick={handleSubmit(_onSubmit)}
              >
                {nextButtonText}
              </Button>
            </HStack>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EditScheduledUpdateModal;
